import React, { useState, useEffect } from 'react';
import { calendarApi, projectApi } from '../services/api';
import { Card, Select, Spinner, Badge } from '../components/UI';
import { CalendarBlank, Flag, ListChecks, UsersThree, CaretLeft, CaretRight } from '@phosphor-icons/react';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function CalendarPage() {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => { projectApi.list().then(r => setProjects(r.data)).finally(() => setLoading(false)); }, []);

  useEffect(() => {
    calendarApi.events(selectedProject || undefined).then(r => setEvents(r.data));
  }, [selectedProject]);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

  const getEventsForDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => {
      const start = e.start ? e.start.substring(0, 10) : '';
      const end = e.end ? e.end.substring(0, 10) : start;
      return dateStr >= start && dateStr <= end;
    });
  };

  const typeIcon = { task: ListChecks, milestone: Flag, meeting: UsersThree };
  const typeColor = { task: '#002FA7', milestone: '#FFAB00', meeting: '#00C853' };

  if (loading) return <div className="flex justify-center py-8"><Spinner size="lg" /></div>;

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="space-y-4" data-testid="calendar-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0A0A]">Calendar</h1>
          <p className="text-sm text-[#666]">Tasks, milestones & meetings on a calendar view</p>
        </div>
        <Select value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)} className="w-64" data-testid="select-project">
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
      </div>

      {/* Legend */}
      <div className="flex gap-4 text-xs">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#002FA7]" /> Tasks</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#FFAB00]" /> Milestones</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#00C853]" /> Meetings</span>
      </div>

      <Card className="p-4" data-testid="calendar-grid">
        <div className="flex items-center justify-between mb-4">
          <button onClick={prevMonth} className="p-2 hover:bg-[#F0F0F0] rounded" data-testid="prev-month"><CaretLeft className="w-5 h-5" /></button>
          <h2 className="text-lg font-bold">{MONTHS[month]} {year}</h2>
          <button onClick={nextMonth} className="p-2 hover:bg-[#F0F0F0] rounded" data-testid="next-month"><CaretRight className="w-5 h-5" /></button>
        </div>

        <div className="grid grid-cols-7 gap-px bg-[#E0E0E0] border border-[#E0E0E0] rounded-lg overflow-hidden">
          {DAYS.map(d => (
            <div key={d} className="bg-[#F9F9F9] px-2 py-2 text-center text-[10px] font-bold text-[#999] uppercase">{d}</div>
          ))}
          {cells.map((day, i) => {
            const dayEvents = day ? getEventsForDay(day) : [];
            const isToday = day && new Date().toDateString() === new Date(year, month, day).toDateString();
            return (
              <div key={i} className={`bg-white min-h-[80px] p-1 ${!day ? 'bg-[#FAFAFA]' : ''}`}>
                {day && (
                  <>
                    <span className={`text-xs font-medium ${isToday ? 'bg-[#002FA7] text-white px-1.5 py-0.5 rounded-full' : 'text-[#666]'}`}>{day}</span>
                    <div className="mt-0.5 space-y-0.5">
                      {dayEvents.slice(0, 3).map((e, idx) => (
                        <div key={idx} className="text-[9px] px-1 py-0.5 rounded truncate text-white" style={{ backgroundColor: typeColor[e.type] || '#666' }} title={e.title}>
                          {e.title}
                        </div>
                      ))}
                      {dayEvents.length > 3 && <span className="text-[9px] text-[#999]">+{dayEvents.length - 3} more</span>}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
